import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Download, FileText } from 'lucide-react';
import { useBranding } from '@/contexts/BrandingContext';
import { generateInvoicePdf } from '@/lib/letterheadPdf';
import PdfPreview from '@/components/PdfPreview';
import ProposalCanvas, { buildDefaultInvoiceBoxes } from '@/components/ProposalCanvas';

// Same letterhead as the proposal, titled INVOICE, no scope/terms, bank always
// included. The body is the free-box canvas: drag/resize/edit anything.
export default function InvoiceDialog({ project, open, onClose }) {
  const { branding } = useBranding();
  const [settings, setSettings] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [boxes, setBoxesRaw] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [dirty, setDirty] = useState(false); // stop reseeding once the canvas is touched
  const [finalProposal, setFinalProposal] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const setBoxes = (updater) => { setDirty(true); setBoxesRaw(updater); };

  useEffect(() => { if (open) api.get('/settings').then(r => setSettings(r.data)).catch(() => {}); }, [open]);

  // The final (highest-version) proposal in the linked proposal's lineage is
  // what the client agreed to, so its total is what the invoice bills.
  useEffect(() => {
    if (!open) { setFinalProposal(null); return; }
    const link = project?.linked_proposal;
    if (!link?.lead_id) { setFinalProposal(null); return; }
    api.get(`/leads/${link.lead_id}/proposals`)
      .then((r) => {
        const all = r.data || [];
        const linked = all.find(p => p.id === link.proposal_id);
        const lineage = linked?.root_id || link.proposal_id;
        const versions = all.filter(p => (p.root_id || p.id) === lineage);
        const pool = versions.length ? versions : (linked ? [linked] : []);
        // Prefer an Approved version; otherwise the highest version number.
        const approved = pool.filter(p => p.status === 'Approved');
        const pick = (approved.length ? approved : pool)
          .sort((a, b) => (b.version || 1) - (a.version || 1))[0];
        setFinalProposal(pick || null);
      })
      .catch(() => setFinalProposal(null));
  }, [open, project]);

  useEffect(() => {
    if (!open) { setInvoice(null); setBoxesRaw([]); setDirty(false); setPageCount(1); setPreviewData(null); return; }
    const t = settings?.invoice_template || {};
    const proposalTotal = Number(finalProposal?.total_amount) || 0;
    const amount = proposalTotal || Number(project?.design_fee?.totalContractValue) || Number(project?.budget) || 0;
    setInvoice({
      invoice_number: `${t.number_prefix || 'INV'}-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
      date: new Date().toLocaleDateString('en-GB'),
      client_name: project?.client_name || '',
      fee_table: [{ id: 1, description: `${t.default_description || 'Interior design & execution services'} — ${project?.name || ''}`, qty: '', rate: 'L.S.', price: amount }],
      gst_included: !!finalProposal?.gst_included,
      gst_rate: Number(finalProposal?.gst_rate) || 18,
      sign_name: settings?.proposal_templates?.sign_name || '',
      closing_note: t.closing_note || 'Thank you for your business.',
    });
  }, [open, project, settings, finalProposal]);

  // Seed the canvas from the header fields; reseed on field edits until the
  // user customizes the layout, then leave their boxes alone. Depends only on
  // the header fields — NOT the whole invoice — so fee-table typing doesn't
  // remount the canvas (which would drop input focus every keystroke).
  useEffect(() => {
    if (!open || !invoice || dirty) return;
    const seeded = buildDefaultInvoiceBoxes({
      date: invoice.date,
      client_name: invoice.client_name,
      invoice_number: invoice.invoice_number,
      closing_note: invoice.closing_note,
    });
    setBoxesRaw(seeded.boxes);
    setPageCount(seeded.pageCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dirty, invoice?.invoice_number, invoice?.date, invoice?.client_name, invoice?.closing_note]);

  if (!invoice) return null;

  const subtotal = invoice.fee_table.reduce((s, r) => s + (Number(r.price) || 0), 0);
  const gstAmount = invoice.gst_included ? Math.round(subtotal * (invoice.gst_rate || 18) / 100) : 0;
  const grandTotal = subtotal + gstAmount;

  const updateFeeRow = (idx, key, val) => {
    setInvoice(prev => {
      const rows = [...prev.fee_table];
      rows[idx] = { ...rows[idx], [key]: val };
      if (key === 'qty' || key === 'rate') {
        const q = Number(rows[idx].qty), rt = Number(rows[idx].rate);
        if (!isNaN(q) && !isNaN(rt) && rows[idx].rate !== '' && rows[idx].qty !== '') rows[idx].price = q * rt;
      }
      return { ...prev, fee_table: rows };
    });
  };
  const addFeeRow = () => setInvoice(prev => ({ ...prev, fee_table: [...prev.fee_table, { id: Date.now(), description: '', qty: '', rate: 'L.S.', price: 0 }] }));
  const removeFeeRow = (idx) => setInvoice(prev => ({ ...prev, fee_table: prev.fee_table.filter((_, i) => i !== idx) }));

  const buildDoc = () => generateInvoicePdf({
    invoice: {
      ...invoice,
      subtotal, gst_amount: gstAmount, total_amount: grandTotal,
      blocks: boxes, page_count: pageCount,
    },
    branding,
    letterhead: settings?.letterhead,
    bank: settings?.bank_details,
  });

  const download = async () => {
    setGenerating(true);
    try {
      const doc = await buildDoc();
      doc.save(`Invoice-${invoice.invoice_number}.pdf`);
      toast.success('Invoice PDF downloaded');
    } catch (e) { console.error(e); toast.error('Invoice generation failed'); }
    finally { setGenerating(false); }
  };

  const openPreview = async () => {
    try {
      const doc = await buildDoc();
      setPreviewData(doc.output('arraybuffer'));
    } catch (e) { console.error(e); toast.error('Preview failed'); }
  };

  const hasBank = settings?.bank_details && Object.values(settings.bank_details).some(Boolean);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-2xl max-w-[780px]" data-testid="invoice-dialog">
        <DialogHeader><DialogTitle className="font-['Manrope']">Create Invoice</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><Label className="text-[10px] text-[#6B7280] uppercase">Invoice No.</Label>
              <Input value={invoice.invoice_number} onChange={e => setInvoice({ ...invoice, invoice_number: e.target.value })} className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" data-testid="invoice-number" /></div>
            <div><Label className="text-[10px] text-[#6B7280] uppercase">Date</Label>
              <Input value={invoice.date} onChange={e => setInvoice({ ...invoice, date: e.target.value })} className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" /></div>
            <div><Label className="text-[10px] text-[#6B7280] uppercase">Billed To</Label>
              <Input value={invoice.client_name} onChange={e => setInvoice({ ...invoice, client_name: e.target.value })} className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" /></div>
          </div>

          <div className={`text-[11px] rounded-lg px-3 py-2 ${hasBank ? 'bg-[#7DA68A]/10 text-[#5c7d6a]' : 'bg-[#C4727F]/10 text-[#C4727F]'}`}>
            {hasBank ? 'Studio bank details from Settings will be printed on the invoice.' : 'No bank details set — add them in Settings → Bank Details so they appear on the invoice.'}
            {dirty ? ' Layout customised — field changes above no longer rewrite the canvas.' : ''}
          </div>

          <div className="text-[11px] rounded-lg px-3 py-2 bg-[#E8E6E1]/60 text-[#6B7280]" data-testid="invoice-amount-source">
            {finalProposal
              ? <>Amount from the final proposal — <span className="font-medium text-[#2C2C2E]">{finalProposal.title || 'Design Proposal'} v{finalProposal.version || 1}</span>
                  {finalProposal.status ? ` (${finalProposal.status})` : ''}. Edit the fee table below to override.</>
              : (project?.linked_proposal
                  ? 'Linked proposal could not be read — amount taken from the project design fee. Check the fee table below.'
                  : 'No proposal linked to this project — amount taken from the project design fee/budget.')}
          </div>

          <div className="max-h-[58vh] overflow-y-auto rounded-lg bg-[#F1F0EC] p-2">
            <ProposalCanvas
              boxes={boxes} setBoxes={setBoxes} pageCount={pageCount} setPageCount={setPageCount}
              proposal={invoice} setProposal={setInvoice}
              showBank={true} bank={settings?.bank_details}
              subtotal={subtotal} gstAmount={gstAmount} grandTotal={grandTotal}
              updateFeeRow={updateFeeRow} addFeeRow={addFeeRow} removeFeeRow={removeFeeRow}
            />
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 h-9 rounded-lg text-xs text-[#6B7280] hover:bg-[#E8E6E1]">Cancel</button>
            <button onClick={openPreview} className="px-4 h-9 rounded-lg bg-[#E8E6E1] text-[#2C2C2E] text-xs font-medium hover:bg-[#D4D0C8] flex items-center gap-1.5" data-testid="invoice-preview">
              <FileText size={14} /> Preview
            </button>
            <button onClick={download} disabled={generating} className="px-4 h-9 rounded-lg bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] disabled:opacity-50 flex items-center gap-1.5" data-testid="invoice-download">
              <Download size={14} /> {generating ? 'Generating…' : 'Download Invoice PDF'}
            </button>
          </div>
        </div>
      </DialogContent>

      <Dialog open={!!previewData} onOpenChange={(o) => { if (!o) setPreviewData(null); }}>
        <DialogContent className="rounded-2xl max-w-3xl" data-testid="invoice-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2"><FileText size={18} strokeWidth={1.5} className="text-[#3A8C8C]" /> Invoice Preview</DialogTitle>
          </DialogHeader>
          <div className="max-h-[75vh] overflow-y-auto rounded-lg bg-[#F1F0EC] p-4">
            <PdfPreview data={previewData} />
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
